"use client"

import {useUiControlLabels} from "@/components/i18n/ui-control-labels-context"
import { useCallback, useEffect, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Loader2, ExternalLink, X, ChevronRight } from "lucide-react"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useAuth } from "@/hooks/use-auth"
import { cn } from "@/lib/utils"
import { formatCurrency } from "./attribution-dashboard/utils"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"
import { useEconomicsSettings } from "@/hooks/settings/use-tenant"
import type { CogsSource } from "@/lib/shopify/economics/effective-cogs"




interface OrderDetail {
  order: {
    shopify_order_id: string
    name: string | null
    created_at_shopify: string | null
    financial_status: string | null
    fulfillment_status: string | null
    total_price: number | null
    currency: string | null
    customer_email: string | null
    customer_first_name: string | null
    customer_last_name: string | null
    raw_json: Record<string, unknown> | null
  }
  lineItems: {
    line_item_id: number
    title: string
    variant_title: string | null
    sku: string | null
    quantity: number
    unit_price: number
    total_discount: number | null
    line_total: number
    properties: Array<{ name?: string; value?: unknown }> | null
    refund_quantity: number | null
    refund_amount: number | null
    /** Effective per-unit COGS; resolved only when `cogs_source` is present. */
    unit_cost: number | null
    /** Absent when the tenant has no Shopify Insights access. */
    cogs_source?: CogsSource
  }[]
  attribution: {
    first_landing_page: string | null
    last_landing_page: string | null
    first_utm_source: string | null
    last_utm_source: string | null
    first_utm_medium: string | null
    last_utm_medium: string | null
    first_utm_campaign: string | null
    last_utm_campaign: string | null
    first_referrer_url: string | null
    last_referrer_url: string | null
    discount_codes_json: unknown
  } | null
  crmLink: {
    crm_entity_type: string
    crm_entity_id: string
    external_url: string | null
    metadata: Record<string, unknown> | null
  } | null
}

/** Parse a numeric value that may arrive as a number or a Shopify price string. */
function toNum(value: unknown): number | null {
  if (value == null) return null
  const n = typeof value === "number" ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/** Format a date as "May 18, 2026" in the given timezone. */
function formatDate(value: string, timezone: string, dateLocale: string = "en-US"): string {
  try {
    return new Date(value).toLocaleDateString(dateLocale, {
      timeZone: timezone,
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  } catch {
    return value
  }
}

/** Format a date + time as "May 18, 2026, 1:31 PM" in the given timezone. */
function formatDateTime(value: string, timezone: string, dateLocale: string = "en-US"): string {
  try {
    return new Date(value).toLocaleString(dateLocale, {
      timeZone: timezone,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
  } catch {
    return new Date(value).toLocaleString(dateLocale)
  }
}

/** A Shopify `discount_applications[]` entry (REST shape). */
interface RawDiscountApplication {
  type?: string // "manual" | "discount_code" | "automatic" | "script"
  title?: string
  code?: string
  value?: string
  value_type?: string // "percentage" | "fixed_amount"
}

type OrderDetailTranslate = ReturnType<typeof useTranslations<"Shopify.ShopifyOrderDetailDrawer">>

/** Human-readable label for a discount application, e.g. "Manual discount · 100% off". */
function discountAppLabel(app: RawDiscountApplication, t: OrderDetailTranslate): string {
  const name = app.code?.trim() || app.title?.trim() || discountTypeLabel(app.type, t)
  const pct = app.value_type === "percentage" ? toNum(app.value) : null
  return pct != null ? t("discountOff", {name, percent: pct}) : name
}

function discountTypeLabel(type: string | undefined, t: OrderDetailTranslate): string {
  switch (type) {
    case "manual": return t("discountManual")
    case "discount_code": return t("discountCode")
    case "automatic": return t("discountAutomatic")
    case "script": return t("discountScript")
    default: return t("discountGeneric")
  }
}

/**
 * Map line_item_id → discount label(s), resolved from the order's
 * `discount_applications` and each line's `discount_allocations[].discount_application_index`.
 * Returns an empty map when the raw payload lacks this detail (e.g. GraphQL-synced orders).
 */
function buildDiscountLabels(rawJson: Record<string, unknown> | null, t: OrderDetailTranslate): Map<number, string> {
  const result = new Map<number, string>()
  const apps = Array.isArray(rawJson?.discount_applications)
    ? (rawJson.discount_applications as RawDiscountApplication[])
    : []
  const rawLineItems = Array.isArray(rawJson?.line_items)
    ? (rawJson.line_items as Array<{
        id?: number
        discount_allocations?: Array<{ discount_application_index?: number }>
      }>)
    : []
  for (const rli of rawLineItems) {
    if (rli?.id == null) continue
    const allocs = Array.isArray(rli.discount_allocations) ? rli.discount_allocations : []
    const labels = allocs
      .map((a) => apps[a?.discount_application_index ?? -1])
      .filter((app): app is RawDiscountApplication => !!app)
      .map(app => discountAppLabel(app, t))
    if (labels.length > 0) {
      result.set(Number(rli.id), Array.from(new Set(labels)).join(", "))
    }
  }
  return result
}

interface RawFulfillment {
  name?: string
  shipment_status?: string | null
  created_at?: string
  tracking_number?: string | null
  tracking_company?: string | null
  tracking_url?: string | null
  tracking_urls?: string[] | null
  line_items?: Array<{ id?: number; title?: string; sku?: string; quantity?: number }>
}

/** "in_transit" → "In Transit" */
function humanizeStatus(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Map line_item_id → the line's `fulfillment_status` (fulfilled / partial / null).
 * Returns an empty map when the payload carries no fulfillment data — GraphQL-synced
 * orders omit `fulfillments[]` entirely, so we must not mislabel their lines "Unfulfilled".
 * A present key with value `null` means a genuinely unfulfilled line.
 */
function buildLineFulfillmentStatus(
  rawJson: Record<string, unknown> | null,
): Map<number, string | null> {
  const map = new Map<number, string | null>()
  if (!Array.isArray(rawJson?.fulfillments)) return map
  const rawLineItems = Array.isArray(rawJson?.line_items)
    ? (rawJson.line_items as Array<{ id?: number; fulfillment_status?: string | null }>)
    : []
  for (const li of rawLineItems) {
    if (li?.id == null) continue
    map.set(Number(li.id), li.fulfillment_status ?? null)
  }
  return map
}

/** A shipment (fulfillment): its carrier/shipment status, tracking, and the items in it. */
interface Shipment {
  name?: string
  shipmentStatus: string | null
  trackingNumber?: string | null
  trackingCompany?: string | null
  trackingUrl?: string | null
  shippedAt?: string
  items: Array<{ title?: string; quantity?: number }>
}

/** One entry per fulfillment — so shipment/tracking detail isn't repeated on every line. */
function buildShipments(rawJson: Record<string, unknown> | null): Shipment[] {
  const fulfillments = Array.isArray(rawJson?.fulfillments)
    ? (rawJson.fulfillments as RawFulfillment[])
    : []
  return fulfillments.map((f) => ({
    name: f.name,
    shipmentStatus: f.shipment_status ?? null,
    trackingNumber: f.tracking_number ?? null,
    trackingCompany: f.tracking_company ?? null,
    trackingUrl: f.tracking_url ?? (Array.isArray(f.tracking_urls) ? f.tracking_urls[0] : null),
    shippedAt: f.created_at,
    items: (Array.isArray(f.line_items) ? f.line_items : []).map((li) => ({
      title: li.title,
      quantity: li.quantity,
    })),
  }))
}

/**
 * Per-line badge for the FULFILLMENT status (fulfilled / unfulfilled / partial) — NOT
 * the shipment/carrier status (that lives in the order-level Shipments block).
 * `undefined` = no fulfillment data → no badge; `null` = unfulfilled.
 */
function fulfillmentBadge(
  status: string | null | undefined,
  tUi: OrderDetailTranslate,
): { label: string; variant: "secondary" | "outline"; className: string } | null {
  if (status === undefined) return null
  const amber = "border-amber-500 text-amber-600 dark:text-amber-500"
  if (status === null) return { label: tUi("unfulfilled"), variant: "outline", className: amber }
  if (status === "fulfilled") return { label: tUi("fulfilled"), variant: "secondary", className: "" }
  if (status === "partial")
    return { label: tUi("partiallyFulfilled"), variant: "outline", className: amber }
  return { label: humanizeStatus(status), variant: "outline", className: "" }
}

/** Per-line refund/removal, aggregated from the order's `refunds[].refund_line_items[]`. */
interface RefundLineInfo {
  quantity: number
  amount: number
  restockType: string | null // "cancel" (removed) | "return" | "no_restock" | "legacy_restock"
}

/**
 * Map line_item_id → refund/removal totals. REST/webhook payloads carry the full
 * `refunds[]` array (GraphQL-synced raw_json omits it — those rely on DB columns).
 */
function buildRefundInfo(rawJson: Record<string, unknown> | null): Map<number, RefundLineInfo> {
  const map = new Map<number, RefundLineInfo>()
  const refunds = Array.isArray(rawJson?.refunds)
    ? (rawJson.refunds as Array<{
        refund_line_items?: Array<{
          line_item_id?: number
          line_item?: { id?: number }
          quantity?: number
          subtotal?: unknown
          restock_type?: string
        }>
      }>)
    : []
  for (const refund of refunds) {
    const items = Array.isArray(refund?.refund_line_items) ? refund.refund_line_items : []
    for (const rli of items) {
      const id = rli.line_item_id ?? rli.line_item?.id
      if (id == null) continue
      const key = Number(id)
      const existing = map.get(key) ?? { quantity: 0, amount: 0, restockType: null }
      map.set(key, {
        quantity: existing.quantity + (rli.quantity ?? 0),
        amount: existing.amount + (toNum(rli.subtotal) ?? 0),
        restockType: rli.restock_type ?? existing.restockType,
      })
    }
  }
  return map
}

/** Label for a refunded/removed line based on Shopify's restock type. */
function refundLabel(restockType: string | null, tUi: OrderDetailTranslate): string {
  if (restockType === "cancel") return tUi("removed")
  if (restockType === "return") return tUi("returned")
  return tUi("refunded")
}

interface OrderDetailDrawerProps {
  orderId: string | null
  integrationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  timezone?: string
}

export function OrderDetailDrawer({
  orderId,
  integrationId,
  open,
  onOpenChange,
  timezone = "UTC",
}: OrderDetailDrawerProps) {
  const {locale: uiDateLocale} = useUiControlLabels()


  const tUi = useTranslations("Shopify.ShopifyOrderDetailDrawer")

  const { fetchWithAuth, isAuthenticated } = useAuth()
  const { settings: economicsSettings } = useEconomicsSettings()
  const [detail, setDetail] = useState<OrderDetail | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())

  const toggleItem = useCallback((lineItemId: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (next.has(lineItemId)) next.delete(lineItemId)
      else next.add(lineItemId)
      return next
    })
  }, [])

  const fetchDetail = useCallback(async () => {
    if (!orderId || !integrationId || !isAuthenticated) return
    setIsLoading(true)
    try {
      const res = await fetchWithAuth(
        `/api/integrations/shopify/orders/${orderId}?integration_id=${integrationId}`,
      )
      if (res.ok) {
        setDetail(await res.json())
      } else {
        setDetail(null)
      }
    } catch (err) {
      console.error("[OrderDetailDrawer] Error:", err)
      setDetail(null)
    } finally {
      setIsLoading(false)
    }
  }, [orderId, integrationId, isAuthenticated, fetchWithAuth])

  useEffect(() => {
    setExpandedItems(new Set())
    if (open && orderId) {
      void fetchDetail()
    } else {
      setDetail(null)
    }
  }, [open, orderId, fetchDetail])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open, onOpenChange])

  const order = detail?.order
  const lineItems = detail?.lineItems ?? []
  const attribution = detail?.attribution
  const crmLink = detail?.crmLink
  const currency = order?.currency ?? undefined

  // Extract shipping address from raw_json
  const rawJson = order?.raw_json as Record<string, unknown> | null
  const shipping = rawJson?.shipping_address as Record<string, unknown> | null

  // Order money breakdown, derived from the raw payload (present for both webhook
  // and GraphQL-synced orders). Shipping lives in different fields per source.
  const shippingTotal =
    toNum(rawJson?.total_shipping_price) ?? // flat field (GraphQL-synced orders)
    toNum(
      (rawJson?.total_shipping_price_set as { shop_money?: { amount?: unknown } } | undefined)
        ?.shop_money?.amount, // money set (REST/webhook orders)
    )
  // Subtotal/shipping/tax are the ORIGINAL amounts; the running total subtracts any
  // refund so it lands on the CURRENT total (matches Shopify + the orders list, which
  // store `current_total_price`). Refunds/removals happen after order edits.
  const subtotal = toNum(rawJson?.subtotal_price)
  const discountTotal = toNum(rawJson?.total_discounts)
  const taxTotal = toNum(rawJson?.total_tax)
  const originalTotal = toNum(rawJson?.total_price)
  const orderTotal =
    toNum(rawJson?.current_total_price) ?? originalTotal ?? order?.total_price
  const refundedTotal =
    originalTotal != null && orderTotal != null && originalTotal > orderTotal
      ? originalTotal - orderTotal
      : null
  // Gross line-items total (before discounts), shown only when a discount applies.
  const itemsGross =
    subtotal != null && discountTotal != null && discountTotal > 0
      ? subtotal + discountTotal
      : null
  // Per-line discount reason (manual / code / automatic + value), from raw_json.
  const discountLabels = buildDiscountLabels(rawJson, tUi)
  // Per-line fulfillment status (fulfilled / unfulfilled), from raw_json.
  const lineFulfillment = buildLineFulfillmentStatus(rawJson)
  // Shipments (one per fulfillment) with tracking, from raw_json.
  const shipments = buildShipments(rawJson)
  // Per-line refunds/removals (qty, amount, restock type), from raw_json.
  const refundInfo = buildRefundInfo(rawJson)

  return (
    <div className="pointer-events-none fixed inset-0 z-40 mx-auto max-w-[1920px] overflow-hidden">
      {/* Backdrop */}
      {open && (
        <div
          className="pointer-events-auto absolute inset-0 bg-black/20"
          onClick={() => onOpenChange(false)}
        />
      )}

      {/* Panel */}
      <div
        className={[
          "pointer-events-auto absolute inset-y-0 right-0 flex w-[480px] max-w-full flex-col border-l bg-background shadow-xl transition-transform duration-300 ease-in-out",
          open ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold">
              {order?.name || orderId || tUi("orderDetail")}
            </h2>
            {order?.created_at_shopify && (
              <p className="text-sm text-muted-foreground">
                {formatDateTime(order.created_at_shopify, timezone, uiDateLocale)}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="ml-2 h-8 w-8 shrink-0"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !detail ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <span key={tUi("orderNotFound")}>{tUi("orderNotFound")}</span></div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="space-y-6 px-6 py-4">
              {/* Order: status + money breakdown */}
              <section>
                <h3 className="mb-2 text-sm font-semibold"><span key={tUi("order")}>{tUi("order")}</span></h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground"><span key={tUi("status")}>{tUi("status")}</span></span>
                    <div className="flex gap-1">
                      {order?.financial_status && (
                        <Badge variant="outline">{order.financial_status}</Badge>
                      )}
                      {order?.fulfillment_status && (
                        <Badge variant="secondary">{order.fulfillment_status}</Badge>
                      )}
                    </div>
                  </div>
                  {itemsGross != null && discountTotal != null && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground"><span key={tUi("items")}>{tUi("items")}</span></span>
                        <span>{formatCurrency(itemsGross, currency)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground"><span key={tUi("discount")}>{tUi("discount")}</span></span>
                        <span>−{formatCurrency(discountTotal, currency)}</span>
                      </div>
                    </>
                  )}
                  {subtotal != null && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground"><span key={tUi("subtotal")}>{tUi("subtotal")}</span></span>
                      <span>{formatCurrency(subtotal, currency)}</span>
                    </div>
                  )}
                  {shippingTotal != null && shippingTotal !== 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground"><span key={tUi("shipping")}>{tUi("shipping")}</span></span>
                      <span>{formatCurrency(shippingTotal, currency)}</span>
                    </div>
                  )}
                  {taxTotal != null && taxTotal !== 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground"><span key={tUi("tax")}>{tUi("tax")}</span></span>
                      <span>{formatCurrency(taxTotal, currency)}</span>
                    </div>
                  )}
                  {refundedTotal != null && refundedTotal > 0 && (
                    <div className="flex justify-between text-destructive">
                      <span><span key={tUi("refunded")}>{tUi("refunded")}</span></span>
                      <span>−{formatCurrency(refundedTotal, currency)}</span>
                    </div>
                  )}
                  <div className="mt-1 flex justify-between border-t pt-1 font-medium">
                    <span><span key={tUi("total")}>{tUi("total")}</span></span>
                    <span>
                      {orderTotal != null ? formatCurrency(orderTotal, currency) : "-"}
                    </span>
                  </div>
                </div>
              </section>

              {/* Customer */}
              <section>
                <h3 className="mb-2 text-sm font-semibold"><span key={tUi("customer")}>{tUi("customer")}</span></h3>
                <div className="space-y-1 text-sm">
                  <div>
                    {[order?.customer_first_name, order?.customer_last_name]
                      .filter(Boolean)
                      .join(" ") || "-"}
                  </div>
                  {order?.customer_email && (
                    <div className="text-muted-foreground">{order.customer_email}</div>
                  )}
                  {shipping && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {[
                        shipping.address1,
                        shipping.city,
                        shipping.province,
                        shipping.zip,
                        shipping.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  )}
                </div>
              </section>

              {/* Line Items */}
              {lineItems.length > 0 && (
                <section>
                  <h3 className="mb-2 text-sm font-semibold">
                    <span><span key={tUi("lineItems")}>{tUi("lineItems")}</span></span>{lineItems.length}<span>)</span>
                  </h3>
                  <ul className="divide-y rounded-md border">
                    {lineItems.map((item) => {
                      const rawRefund = refundInfo.get(item.line_item_id)
                      const refundQty = rawRefund?.quantity ?? item.refund_quantity ?? 0
                      const refundAmount = rawRefund?.amount ?? item.refund_amount ?? 0
                      const itemRefundLabel = refundLabel(rawRefund?.restockType ?? null, tUi)
                      const discount = item.total_discount ?? 0
                      const fullyRefunded = refundQty > 0 && refundQty >= item.quantity
                      const partiallyRefunded = refundQty > 0 && refundQty < item.quantity
                      const discountLabel = discountLabels.get(item.line_item_id)
                      // Line fully covered by a discount (not a refund) — customer paid nothing.
                      const fullyDiscounted =
                        discount > 0 && refundQty === 0 && item.line_total === 0
                      const fulfBadge = fulfillmentBadge(
                        lineFulfillment.has(item.line_item_id)
                          ? lineFulfillment.get(item.line_item_id)
                          : undefined, tUi
                      )
                      const properties = Array.isArray(item.properties)
                        ? item.properties.filter(
                            (p): p is { name: string; value: unknown } =>
                              !!p &&
                              typeof p.name === "string" &&
                              !p.name.startsWith("_") &&
                              p.value != null &&
                              String(p.value).trim() !== "",
                          )
                        : []
                      const hasDetails = properties.length > 0 || discount > 0 || refundQty > 0
                      const isOpen = expandedItems.has(item.line_item_id)
                      return (
                        <li
                          key={item.line_item_id}
                          className={cn(fullyRefunded && "bg-destructive/5")}
                        >
                          <div
                            role={hasDetails ? "button" : undefined}
                            tabIndex={hasDetails ? 0 : undefined}
                            aria-expanded={hasDetails ? isOpen : undefined}
                            onClick={hasDetails ? () => toggleItem(item.line_item_id) : undefined}
                            onKeyDown={
                              hasDetails
                                ? (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault()
                                      toggleItem(item.line_item_id)
                                    }
                                  }
                                : undefined
                            }
                            className={cn(
                              "flex items-start gap-2 px-3 py-2",
                              hasDetails && "cursor-pointer hover:bg-muted/50",
                            )}
                          >
                            {hasDetails ? (
                              <ChevronRight
                                className={cn(
                                  "mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                                  isOpen && "rotate-90",
                                )}
                              />
                            ) : (
                              <span className="w-3.5 shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div
                                className={cn(
                                  "break-words text-sm",
                                  fullyRefunded && "text-muted-foreground line-through",
                                )}
                              >
                                {item.title}
                              </div>
                              {item.variant_title && (
                                <div className="break-words text-xs text-muted-foreground">
                                  {item.variant_title}
                                </div>
                              )}
                              {item.sku && (
                                <div className="break-all text-xs text-muted-foreground">
                                  <span><span key={"SKU:"}>{"SKU:"}</span></span> {item.sku}
                                </div>
                              )}
                              {item.cogs_source && (
                                <LineCogs
                                  source={item.cogs_source}
                                  unitCost={item.unit_cost}
                                  quantity={item.quantity}
                                  defaultCogsPerUnit={economicsSettings.default_cogs_per_unit}
                                  currency={currency}
                                />
                              )}
                              {(fullyRefunded ||
                                partiallyRefunded ||
                                fullyDiscounted ||
                                (fulfBadge && !fullyRefunded)) && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {fullyRefunded && (
                                    <Badge variant="destructive">{itemRefundLabel}</Badge>
                                  )}
                                  {partiallyRefunded && (
                                    <Badge
                                      variant="outline"
                                      className="border-amber-500 text-amber-600 dark:text-amber-500"
                                    >
                                      {itemRefundLabel} <span>·</span> {refundQty}
                                    </Badge>
                                  )}
                                  {fullyDiscounted && !fullyRefunded && (
                                    <Badge variant="secondary"><span key={tUi("free")}>{tUi("free")}</span></Badge>
                                  )}
                                  {/* A fully removed/refunded line isn't "Unfulfilled" — hide that badge */}
                                  {fulfBadge && !fullyRefunded && (
                                    <Badge variant={fulfBadge.variant} className={fulfBadge.className}>
                                      {fulfBadge.label}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="shrink-0 text-right text-sm">
                              <div className="text-xs text-muted-foreground">
                                {item.quantity} <span>×</span> {formatCurrency(item.unit_price, currency)}
                              </div>
                              <div
                                className={cn(
                                  "font-medium",
                                  fullyRefunded && "text-muted-foreground line-through",
                                )}
                              >
                                {formatCurrency(item.line_total, currency)}
                              </div>
                            </div>
                          </div>
                          {isOpen && hasDetails && (
                            <div className="space-y-2 border-t bg-muted/30 px-3 py-2 pl-[34px] text-xs">
                              {properties.length > 0 && (
                                <div>
                                  <div className="mb-1 font-medium"><span key={tUi("properties")}>{tUi("properties")}</span></div>
                                  <dl className="space-y-0.5">
                                    {properties.map((p, i) => (
                                      <div
                                        key={`${p.name}-${i}`}
                                        className="flex justify-between gap-3"
                                      >
                                        <dt className="shrink-0 text-muted-foreground">
                                          {p.name}
                                        </dt>
                                        <dd className="min-w-0 break-all text-right">
                                          {String(p.value)}
                                        </dd>
                                      </div>
                                    ))}
                                  </dl>
                                </div>
                              )}
                              {discount > 0 && (
                                <div className="flex justify-between gap-3">
                                  <span className="text-muted-foreground"><TranslationText>{tUi.rich("discountValue0", { value0: String(discountLabel ? tUi("value0", { value0: String(discountLabel) }) : "") })}</TranslationText></span>
                                  <span className="shrink-0">
                                    −{formatCurrency(discount, currency)}
                                  </span>
                                </div>
                              )}
                              {refundQty > 0 && (
                                <div className="flex justify-between text-destructive">
                                  <span>
                                    {itemRefundLabel} ({refundQty}{" "}
                                    <span key={refundQty === 1 ? tUi("unit") : tUi("units")}>{refundQty === 1 ? tUi("unit") : tUi("units")}</span>)
                                  </span>
                                  <span>−{formatCurrency(refundAmount, currency)}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )}

              {/* Shipments — one entry per fulfillment (tracking lives here, not per line) */}
              {shipments.length > 0 && (
                <section>
                  <h3 key={shipments.length > 1 ? tUi("shipmentsValue0", { value0: String(shipments.length) }) : tUi("shipment")} className="mb-2 text-sm font-semibold">
                    {shipments.length > 1 ? tUi("shipmentsValue0", { value0: String(shipments.length) }) : tUi("shipment")}
                  </h3>
                  <ul className="space-y-3">
                    {shipments.map((s, i) => {
                      const delivered = (s.shipmentStatus ?? "").toLowerCase() === "delivered"
                      return (
                        <li key={s.name ?? i} className="rounded-md border p-3 text-sm">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <span className="font-medium">{s.name ?? `Shipment ${i + 1}`}</span>
                            <Badge
                              variant="outline"
                              className={cn(
                                delivered &&
                                  "border-green-600 text-green-700 dark:border-green-500 dark:text-green-500",
                              )}
                            >
                              <span key={s.shipmentStatus ? humanizeStatus(s.shipmentStatus) : tUi("fulfilled")}>{s.shipmentStatus ? humanizeStatus(s.shipmentStatus) : tUi("fulfilled")}</span>
                            </Badge>
                          </div>
                          {(s.trackingCompany || s.trackingNumber) && (
                            <div className="text-xs">
                              <span className="text-muted-foreground"><span key={tUi("tracking")}>{tUi("tracking")}</span></span>
                              {s.trackingUrl ? (
                                <a
                                  href={s.trackingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="break-all underline decoration-dotted hover:text-primary"
                                >
                                  {[s.trackingCompany, s.trackingNumber].filter(Boolean).join(" ")}
                                </a>
                              ) : (
                                <span className="break-all">
                                  {[s.trackingCompany, s.trackingNumber].filter(Boolean).join(" ")}
                                </span>
                              )}
                            </div>
                          )}
                          {s.shippedAt && (
                            <div className="text-xs text-muted-foreground">
                              <span><span key={tUi("shipped")}>{tUi("shipped")}</span></span> {formatDate(s.shippedAt, timezone, uiDateLocale)}
                            </div>
                          )}
                          {s.items.length > 0 && (
                            <ol className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                              {s.items.map((it, j) => (
                                <li key={j} className="flex justify-between gap-2">
                                  <span className="min-w-0 break-words">
                                    {j + 1}. {it.title}
                                  </span>
                                  {it.quantity != null && (
                                    <span className="shrink-0">× {it.quantity}</span>
                                  )}
                                </li>
                              ))}
                            </ol>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )}

              {/* Attribution Journey */}
              {attribution && (
                <section>
                  <h3 className="mb-2 text-sm font-semibold"><span key={tUi("attributionJourney")}>{tUi("attributionJourney")}</span></h3>
                  <div className="space-y-2 text-sm">
                    {attribution.first_landing_page && (
                      <div>
                        <span className="text-muted-foreground"><span key={tUi("firstLanding")}>{tUi("firstLanding")}</span></span>
                        <span className="break-all">{attribution.first_landing_page}</span>
                      </div>
                    )}
                    {attribution.last_landing_page && (
                      <div>
                        <span className="text-muted-foreground"><span key={tUi("lastLanding")}>{tUi("lastLanding")}</span></span>
                        <span className="break-all">{attribution.last_landing_page}</span>
                      </div>
                    )}
                    {(attribution.first_utm_source || attribution.last_utm_source) && (
                      <div>
                        <span className="text-muted-foreground"><span key={tUi("uTMSource")}>{tUi("uTMSource")}</span></span>
                        <span>
                          {attribution.last_utm_source || attribution.first_utm_source}
                        </span>
                      </div>
                    )}
                    {(attribution.first_utm_medium || attribution.last_utm_medium) && (
                      <div>
                        <span className="text-muted-foreground"><span key={tUi("uTMMedium")}>{tUi("uTMMedium")}</span></span>
                        <span>
                          {attribution.last_utm_medium || attribution.first_utm_medium}
                        </span>
                      </div>
                    )}
                    {(attribution.first_utm_campaign || attribution.last_utm_campaign) && (
                      <div>
                        <span className="text-muted-foreground"><span key={tUi("uTMCampaign")}>{tUi("uTMCampaign")}</span></span>
                        <span>
                          {attribution.last_utm_campaign || attribution.first_utm_campaign}
                        </span>
                      </div>
                    )}
                    {(attribution.first_referrer_url || attribution.last_referrer_url) && (
                      <div>
                        <span className="text-muted-foreground"><span key={tUi("referrer")}>{tUi("referrer")}</span></span>
                        <span className="break-all">
                          {attribution.last_referrer_url || attribution.first_referrer_url}
                        </span>
                      </div>
                    )}
                    {Array.isArray(attribution.discount_codes_json) &&
                      attribution.discount_codes_json.length > 0 && (
                        <div>
                          <span className="text-muted-foreground"><span key={tUi("discounts")}>{tUi("discounts")}</span></span>
                          {(
                            attribution.discount_codes_json as {
                              code?: string
                              title?: string
                            }[]
                          )
                            // Manual/automatic discounts have no `code`, only a `title`
                            // (e.g. "Custom discount"); fall back so the line isn't blank.
                            .map((dc) => dc.code || dc.title)
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}
                  </div>
                </section>
              )}

              {/* CRM Link */}
              {crmLink && (
                <section>
                  <h3 className="mb-2 text-sm font-semibold"><span key={tUi("cRMLink")}>{tUi("cRMLink")}</span></h3>
                  <div className="flex items-center gap-2 text-sm">
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    {crmLink.external_url ? (
                      <a
                        href={crmLink.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline decoration-dotted hover:text-primary"
                      >
                        {crmLink.crm_entity_type}<span>:</span> {crmLink.crm_entity_id}
                      </a>
                    ) : (
                      <span>
                        {crmLink.crm_entity_type}: {crmLink.crm_entity_id}
                      </span>
                    )}
                  </div>
                </section>
              )}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  )
}

type LineCogsSource = Exclude<CogsSource, "unset"> | "default"

/** Line COGS (unit cost × quantity) and its source; like Product Economics, an uncosted line uses the global default. */
function LineCogs({
  source,
  unitCost,
  quantity,
  defaultCogsPerUnit,
  currency,
}: {
  source: CogsSource
  unitCost: number | null
  quantity: number
  defaultCogsPerUnit: number
  currency: string | undefined
}) {
  const tUi = useTranslations("Shopify.ShopifyOrderDetailDrawer")
  const cost = lineCost(source, unitCost, defaultCogsPerUnit)
  return (
    <div className="break-words text-xs text-muted-foreground">
      {cost
        ? tUi("lineCogs", {
          amount: formatCurrency(cost.perUnit * quantity, currency),
          source: cogsSourceLabel(cost.source, tUi),
        })
        : tUi("lineCogsNotSet")}
    </div>
  )
}

function lineCost(
  source: CogsSource,
  unitCost: number | null,
  defaultCogsPerUnit: number,
): { perUnit: number; source: LineCogsSource } | null {
  if (source !== "unset") return unitCost != null ? { perUnit: unitCost, source } : null
  return defaultCogsPerUnit > 0 ? { perUnit: defaultCogsPerUnit, source: "default" } : null
}

function cogsSourceLabel(source: LineCogsSource, tUi: OrderDetailTranslate): string {
  switch (source) {
    case "manual_variant": return tUi("cogsSourceVariantOverride")
    case "manual_product": return tUi("cogsSourceProductOverride")
    case "shopify": return "Shopify"
    case "default": return tUi("cogsSourceDefault")
  }
}
